import { UniversalLink } from '@plone/volto/components';
import calendar from '@redturtle/volto-io-studio/icons/calendar-days-solid.svg';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import {
  Pagination,
  Table,
  Container,
  Form,
  FormField,
} from 'semantic-ui-react';

import { Icon } from '@plone/volto/components';
import paginationLeftSVG from '@plone/volto/icons/left-key.svg';
import paginationRightSVG from '@plone/volto/icons/right-key.svg';
import { Button, SearchField, Label, Input } from 'react-aria-components';
import { defineMessages, useIntl } from 'react-intl';
import './teachings.less';

const messages = defineMessages({
  search: {
    id: 'structurelisting_search',
    defaultMessage: "Cerca per nome dell'insegnamento o del docente",
  },
  descrizione: {
    id: 'structurelisting_descrizione',
    defaultMessage: 'Descrizione',
  },
  docente: {
    id: 'structurelisting_docente',
    defaultMessage: 'Docente',
  },
  orario: {
    id: 'structurelisting_orario',
    defaultMessage: 'Orario delle lezioni',
  },
  code: {
    id: 'structurelisting_code',
    defaultMessage: 'Codice',
  },
  cfu: {
    id: 'structurelisting_cfu',
    defaultMessage: 'CFU',
  },
  periodo: {
    id: 'structurelisting_periodo',
    defaultMessage: 'Periodo',
  },
  ore: {
    id: 'structurelisting_ore',
    defaultMessage: 'Ore',
  },
});

const StructureList = ({ items }) => {
  const intl = useIntl();
  const data = useMemo(() => {
    const hash = (item) =>
      `${item.code}-${item.desc}-${item.doc?.matricola || '-'}`;
    const all = items.map((item) => item.units).flat();
    const hashes = all.map(hash);
    return all.filter((item, index) => hashes.indexOf(hash(item)) === index);
  }, [items]);
  const columns = [
    {
      id: 'code',
      header: intl.formatMessage(messages.code),
      accessorFn: (row) => row.code,
    },
    {
      id: 'desc',
      header: intl.formatMessage(messages.descrizione),
      accessorFn: (row) => row.desc,
      cell: ({ row }) => {
        const data = { desc: row.original.desc, url: row.original.url };
        return data?.url ? (
          <UniversalLink href={data.url}>{data.desc}</UniversalLink>
        ) : (
          data?.desc
        );
      },
      sortingFn: (a, b) => {
        return a.original.desc.localeCompare(b.original.desc);
      },
    },
    {
      id: 'doc',
      header: intl.formatMessage(messages.docente),
      accessorFn: (row) =>
        row.doc ? `${row.doc.cognome}, ${row.doc.nome}` : '',
      cell: ({ row }) => {
        const doc = row.original.doc;
        {
          return (
            doc &&
            (doc.url ? (
              <UniversalLink href={doc.url}>
                {doc.nome} {doc.cognome}
              </UniversalLink>
            ) : (
              `${doc.nome} ${doc.cognome}`
            ))
          );
        }
      },
      sortingFn: (a, b) => {
        const aDoc = a.original.doc
          ? `${a.original.doc.cognome}, ${a.original.doc.nome}`
          : '';
        const bDoc = b.original.doc
          ? `${b.original.doc.cognome}, ${b.original.doc.nome}`
          : '';
        return aDoc.toLowerCase().localeCompare(bDoc.toLowerCase());
      },
    },
    {
      id: 'cfu',
      header: intl.formatMessage(messages.cfu),
      accessorFn: (row) => row.cfu || '-',
      enableSorting: false,
    },
    {
      id: 'ore',
      header: intl.formatMessage(messages.ore),
      accessorFn: (row) => row.ore || '-',
      enableSorting: false,
    },
    {
      id: 'periodo',
      header: intl.formatMessage(messages.periodo),
      accessorFn: (row) => row.periodo?.desc || '-',
      enableSorting: false,
    },
    {
      id: 'timetable',
      header: intl.formatMessage(messages.orario),
      accessorFn: (row) => {
        return { url: row.timetable?.url };
      },
      cell: (props) => {
        const data = props.getValue();
        return (
          data?.url && (
            <UniversalLink
              href={`${data.url}`}
              className="timetable"
              aria-label={intl.formatMessage(messages.orario)}
            >
              <Icon name={calendar} />
            </UniversalLink>
          )
        );
      },
      enableSorting: false,
    },
    // {
    //     id: 'debug',
    //     header: 'Debug',
    //     cell: ({ row }) => {
    //         return <pre>{JSON.stringify(row.original, null, 2)}</pre>
    //     },
    // },
  ];
  const [globalFilter, setGlobalFilter] = useState([]);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // manualFiltering: true,
    getFilteredRowModel: getFilteredRowModel(), //client side filtering
    // globalFilterFn: 'text', // built-in filter function
    globalFilterFn: 'includesString', // built-in filter function
    // globalFilterFn: (props) => {
    initialState: {
      pagination: {
        pageSize: 20,
      },
      sorting: [
        {
          id: 'desc',
          desc: false,
        },
      ],
    },
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
  });
  return (
    <>
      <Form
        className="search"
        style={{
          width: '50%',
          float: 'right',
          paddingBottom: '1em',
          paddingTop: '1em',
        }}
      >
        <SearchField>
          <Label for="structurelist-search">
            {intl.formatMessage(messages.search)}
          </Label>
          <Input
            icon="search"
            value={table.getState().globalFilter}
            id="structurelist-search"
            onChange={(e) => table.setGlobalFilter(String(e.target.value))}
          />{' '}
        </SearchField>
      </Form>

      {/*<Container>
         <SearchField className="block-inner-wrapper">
  <Label>{intl.formatMessage(messages.search)}</Label>
  <Input 
      value={table.getState().globalFilter}
      onChange={e => table.setGlobalFilter(String(e.target.value))}
  />
</SearchField> 
      </Container>*/}
      <Table sortable className="teachins-table">
        <Table.Header>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <Table.HeaderCell
                  style={{
                    width: header.getSize(),
                    whiteSpace: 'inherit',
                  }}
                  key={header.id}
                  sorted={
                    { asc: 'ascending', desc: 'descending' }[
                      header.column.getIsSorted()
                    ]
                  }
                  onClick={header.column.getToggleSortingHandler()}
                  className={'column-' + index}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          ))}
        </Table.Header>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell, index) => (
                <td className={'column-' + index} key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {/* <tfoot>
                    {table.getFooterGroups().map(footerGroup => (
                        <tr key={footerGroup.id}>
                            {footerGroup.headers.map(header => (
                                <th key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.footer,
                                            header.getContext()
                                        )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </tfoot> */}
      </Table>
      {table.getPageCount() > 1 && (
        <div className="pagination-wrapper react-table-pagination">
          <Pagination
            activePage={table.getState().pagination.pageIndex + 1}
            totalPages={table.getPageCount()}
            onPageChange={(e, { activePage }) => {
              table.setPageIndex(activePage - 1);
            }}
            firstItem={null}
            lastItem={null}
            prevItem={{
              content: <Icon name={paginationLeftSVG} size="18px" />,
              icon: true,
              'aria-disabled': table.getState().pagination.pageIndex + 1 === 1,
              className:
                table.getState().pagination.pageIndex + 1 === 1
                  ? 'disabled'
                  : null,
            }}
            nextItem={{
              content: <Icon name={paginationRightSVG} size="18px" />,
              icon: true,
              'aria-disabled':
                table.getState().pagination.pageIndex + 1 ===
                table.getPageCount(),
              className:
                table.getState().pagination.pageIndex + 1 ===
                table.getPageCount()
                  ? 'disabled'
                  : null,
            }}
          ></Pagination>
        </div>
      )}
      {/* <pre>{JSON.stringify(data, null, 2)}</pre>

            <pre>{JSON.stringify(items, null, 2)}</pre> */}
    </>
  );
};

export default StructureList;
